import { apiClient } from "@/lib/apiClient";
import { apiPath } from "@/lib/apiPath";
import { getAuthRegistry } from "@/lib/auth/AuthRegistry";
import { getCompanyRegistry } from "@/lib/company/companyRegistrySingleton";
import { CompanySettings } from "@/lib/types/company";

/**
 * Create a public session for a company (widget flow).
 * Accepts widget_host (e.g. "w1033") extracted from the subdomain.
 * POSTs to /api/v1/public/sessions and stores the returned token in AuthRegistry.
 * Returns the resolved company_id and region from the backend response.
 *
 * NOTE: Does NOT call configureApi() — the caller must handle region switching
 * to ensure the correct apiClient instance is updated.
 */
export async function createPublicSession(widgetHost: string): Promise<{ companyId: number; region?: string }> {
  const { data } = await apiClient.post(apiPath("/public/sessions"), {
    widget_host: widgetHost,
  });

  const accessToken: string | undefined = data?.access_token;
  const region: string | undefined = data?.region;
  const companyId: number | undefined = data?.company_id;

  if (!accessToken) {
    throw new Error("No access_token from /public/sessions");
  }

  if (!companyId) {
    throw new Error("No company_id from /public/sessions");
  }

  // Store token in AuthRegistry so axios interceptor will attach it automatically.
  getAuthRegistry().setAuth(
    { id: "public", email: "widget@public", name: "Widget Public" },
    { accessToken, refreshToken: null }
  );

  return { companyId, region };
}

/**
 * Fetch company settings for the widget (public endpoint).
 * Bearer token is auto-attached by apiClient interceptor.
 */
export async function fetchPublicCompanySettings(): Promise<CompanySettings> {
  const { data } = await apiClient.get(apiPath("/company/settings"));
  const settings = data as CompanySettings;

  // Hydrate the shared CompanyRegistry singleton
  getCompanyRegistry().hydrate(settings);

  return settings;
}

/**
 * Get the API base URL for the current regional shard.
 * Uses the cached widget_region from localStorage.
 */
export function getApiBaseUrl(): string {
  const region = localStorage.getItem("widget_region");
  if (region && region !== "default") {
    return `https://api-${region}.boona.app`;
  }
  return import.meta.env.VITE_API_URL ?? "https://boona.app";
}
